import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Type, ImageIcon } from 'lucide-react'
import { cn } from '@/utils/cn'
import { ImageToVideoForm, TextToVideoForm } from '@/components/features'

type TabId = 'i2v' | 't2v'

const tabs = [
  { id: 'i2v' as const, label: 'Image to Video', shortLabel: 'Image', icon: ImageIcon, path: '/video/i2v' },
  // { id: 'instant' as const, label: 'Instant Videos', shortLabel: 'Instant', icon: Zap, path: '/video/instant' },
  { id: 't2v' as const, label: 'Text to Video', shortLabel: 'Text', icon: Type, path: '/video/t2v' },
]

export function VideoPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>('i2v')

  // Sync tab with URL
  useEffect(() => {
    const path = location.pathname
    if (path.includes('/video/i2v')) {
      setActiveTab('i2v')
    // } else if (path.includes('/video/instant')) {
    //   setActiveTab('instant')
    } else if (path.includes('/video/t2v')) {
      setActiveTab('t2v')
    } else if (path === '/video') {
      // Default to i2v
      navigate('/video/i2v', { replace: true })
    }
  }, [location.pathname, navigate])

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId)
    const tab = tabs.find((t) => t.id === tabId)
    if (tab) {
      navigate(tab.path)
    }
  }

  return (
    <div>
      {/* Tab Navigation */}
      <div className="px-6 md:px-8 pt-6">
        <VideoTabs activeTab={activeTab} onTabChange={handleTabChange} />
      </div>

      {/* Tab Content */}
      <div className="p-6 md:p-8 max-w-4xl mx-auto animate-page-in">
        {activeTab === 'i2v' && <ImageToVideoForm />}
        {/* {activeTab === 'instant' && <InstantVideosForm />} */}
        {activeTab === 't2v' && <TextToVideoForm />}
      </div>
    </div>
  )
}

interface VideoTabsProps {
  activeTab: TabId
  onTabChange: (tabId: TabId) => void
}

function VideoTabs({ activeTab, onTabChange }: VideoTabsProps) {
  return (
    <div className="flex items-center gap-1">
      {tabs.map((tab) => {
        const Icon = tab.icon
        const isActive = activeTab === tab.id
        
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
              isActive
                ? 'text-white bg-white/[0.08] border border-white/[0.1]'
                : 'text-[#666] hover:text-white hover:bg-white/[0.04] border border-transparent'
            )}
          >
            <Icon className="size-4" />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.shortLabel}</span>
          </button>
        )
      })}
    </div>
  )
}
